































































































































































import Vue, { PropType } from 'vue';
import uuid from 'uuid/v4';
import { currencies } from '@/constants';
import finance, { CurrencyCode, getExchangeValue } from '@/services/finance';
import { Price, PriceType } from '@/services/price';
import InfoBox from '@/components/InfoBox.vue';
import FieldBase from '@/components/field/Base.vue';
import LoaderInline from '@/components/LoaderInline.vue';
import PillContent from '@/components/pill/PillContent.vue';
import PricingTable from '@/components/PricingTable.vue';
import {
  IProduct,
  IProductResource,
  IProductResourceCost,
  ResourceType,
} from '@/services/product';
import {
  GridColumn,
  GridForm,
  GridRow,
} from '@/components/grid';

interface IResource extends IProductResource<any> {
  type: ResourceType;
  subTotal: number;
  selectedPrice: Price;
}

interface Recommended {
  display: boolean;
  retail: number;
  wholesale: number;
}

interface Totals {
  other: number;
  resource: number;
  total: number;
}

const formattedCurrencies = Object
  .keys(currencies)
  .reduce((p, key) => {
    const data = (currencies as any)[key];
    p[key] = `${data.name} (${data.symbol_native})`;
    return p;
  }, {} as any);

const RETAIL_MARKUP = 2.5;
const WHOLESALE_MARKUP = 2;

export default Vue.extend({
  props: {
    value: {
      type: Object as PropType<IProduct>,
    },
  },
  mounted() {
    this.fetchRates();
  },
  data: () => ({
    RETAIL_MARKUP,
    WHOLESALE_MARKUP,
    // index for array of resource costs from DB
    // enables multiple cost modelling scenarios in future
    costIndex: 0,
    currencies,
    currencyParams: {
      title: 'Choose a base currency',
      values: formattedCurrencies,
      display: (value: string) => `Currency: ${currencies[value].symbol_native}/${value}`,
    },
    rates: {},
    ratesFail: false,
    ratesLoading: false,
  }),
  computed: {
    localValue: {
      get(): IProduct {
        return this.value;
      },
      set(value: IProduct) {
        this.$emit('input', value);
      },
    },
    baseCurrency: {
      get(): CurrencyCode {
        return this.localValue.resource_costs[this.costIndex].currency;
      },
      set(currency: CurrencyCode) {
        this.localValue.resource_costs[this.costIndex].currency = currency;
      },
    },
    resourceCosts: {
      get(): { [resourceId: string]: number; } {
        return this.localValue.resource_costs[this.costIndex].resources;
      },
      set(value: { [resourceId: string]: number; }) {
        this.localValue.resource_costs[this.costIndex].resources = value;
      },
    },
    others(): number[] {
      return this.localValue.costs.map(({ amount, currency }) => (
        amount && currency
          ? getExchangeValue(amount, currency, this.baseCurrency)
          : 0
      ), 0);
    },
    recommended(): Recommended {
      const { total } = this.totals;
      const wholesale = total * WHOLESALE_MARKUP;
      const retail = wholesale * RETAIL_MARKUP;

      // don't display recommended price if any resource price is missing
      const hasPrice = Math.ceil(total) > 0;
      const allResourcesWithPrice = this.resources.length
        ? !this.resources.find(resource => !resource.selectedPrice)
        : true;

      return {
        display: hasPrice && allResourcesWithPrice,
        wholesale,
        retail,
      };
    },
    resources(): IResource[] {
      const { components, materials } = this.localValue as IProduct;
      return [...materials, ...components]
        // resource might not have been selected yet
        // i.e. dropdown is curently empty
        .filter(resource => !!resource.detail)
        .map(resource => {

          // determine if component or material
          const type = resource.detail.composition === undefined
            ? ResourceType.component
            : ResourceType.material;

          // get subtotal if resource has prices/costs
          const priceIndex = this.resourceCosts[resource.id] || 0;
          const selectedPrice = resource.detail.prices[priceIndex];
          const subTotal = selectedPrice
            ? getExchangeValue(
                resource.qty * selectedPrice.amount,
                selectedPrice.currency,
                this.baseCurrency,
              )
            : 0;

          return {
            ...resource,
            selectedPrice,
            subTotal,
            type,
          };
        });
    },
    totals(): Totals {
      const resource = this.resources.reduce((acc, item) => acc += item.subTotal, 0);
      const other = this.others.reduce((acc, item) => acc += item, 0);
      return {
        // leave total at top to appear top of summary
        total: resource + other,
        resource,
        other,
      };
    },
  },
  methods: {
    copyRecommended() {
      const currentPrices = this.localValue.prices;
      const recommended = ['Wholesale', 'Retail'].map((category, index) => {
        // convert to 2 decimal places and make float
        const recommendedValue = (this.recommended as any)[category.toLowerCase()];
        const amount = parseFloat(recommendedValue.toFixed(2));

        // add to end of prices array
        const newIndex = currentPrices.length + index;
        return {
          id: uuid(),
          type_id: this.localValue.id,

          amount,
          category,
          currency: this.baseCurrency,
          index: newIndex,
          type: 'product' as PriceType,
        };
      });

      this.localValue = {
        ...this.localValue,
        prices: [...currentPrices, ...recommended],
      };

      // hide sidebar in mobile to show prices
      const isMobile = this.$store.state.isMobile;
      const pricingTable = document.getElementById('pricingTable');
      isMobile
        && this.$store.dispatch('chromeEditor/toggleSidebar', false);

      pricingTable
        && pricingTable.scrollIntoView({
            behavior: 'smooth',
            block: isMobile ? 'start' : 'center',
          });
    },
    fetchRates() {
      this.ratesLoading = true;
      finance
        .getRates()
        .then(() => this.ratesFail = false)
        .catch(() => this.ratesFail = true)
        .finally(() => {
          this.ratesLoading = false;

          // trigger computed props to recalculate
          const base = this.baseCurrency;
          this.baseCurrency = '' as CurrencyCode;
          this.baseCurrency = base;
        });
    },
    // update to show next available cost from list of resource prices
    nextCost(rowId: string) {
      const resource = this.resources.find(item => item.id === rowId);
      if (resource) {
        // loop round to first cost when at the end
        const currentIndex = ++this.resourceCosts[rowId] || 1;
        const updatedIndex = currentIndex >= resource.detail.prices.length ? 0 : currentIndex;

        // update resource cost model
        this.resourceCosts = {
          ...this.resourceCosts,
          [rowId]: updatedIndex,
        };
      }
    },
  },
  components: {
    FieldBase,
    GridColumn,
    GridForm,
    GridRow,
    InfoBox,
    LoaderInline,
    PillContent,
    PricingTable,
  },
});
